import React, { useState } from 'react';
import api from '../utils/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: null
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation côté client
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setStatus({
        loading: false,
        success: false,
        error: 'Veuillez remplir tous les champs'
      });
      return;
    }

    // Validation email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setStatus({
        loading: false,
        success: false,
        error: 'Veuillez entrer une adresse email valide'
      });
      return;
    }

    setStatus({ loading: true, success: false, error: null });

    try {
      await api.post('/contact', formData);

      setStatus({
        loading: false,
        success: true,
        error: null
      });

      // Réinitialiser le formulaire
      setFormData({
        name: '',
        email: '',
        message: ''
      });

      // Effacer le message de succès après 5 secondes
      setTimeout(() => {
        setStatus({ loading: false, success: false, error: null });
      }, 5000);

    } catch (err) {
      setStatus({
        loading: false,
        success: false,
        error: err.response?.data?.message || 'Une erreur est survenue. Veuillez réessayer.'
      });
    }
  };

  return (
    <div className="py-16 animate-fade-in">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Contactez-nous
          </h1>
          <div className="w-20 h-1 bg-primary-600 mx-auto mb-6"></div>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Vous avez un projet en tête ? N'hésitez pas à nous contacter.
            Nous vous répondrons dans les plus brefs délais.
          </p>
        </div>

        <div className="flex flex-col items-center max-w-2xl mx-auto">
          {/* Formulaire */}
          <div className="bg-white rounded-lg shadow-lg p-8 w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Envoyez-nous un message
            </h2>

            <form onSubmit={handleSubmit}>
              {/* Nom */}
              <div className="mb-6">
                <label htmlFor="name" className="block text-gray-700 font-semibold mb-2">
                  Nom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-all"
                  placeholder="Votre nom"
                  required
                  maxLength={100}
                />
              </div>

              {/* Email */}
              <div className="mb-6">
                <label htmlFor="email" className="block text-gray-700 font-semibold mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-all"
                  placeholder="votre.email@exemple.com"
                  required
                />
              </div>

              {/* Message */}
              <div className="mb-6">
                <label htmlFor="message" className="block text-gray-700 font-semibold mb-2">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="6"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent outline-none transition-all resize-none"
                  placeholder="Décrivez votre projet..."
                  required
                  maxLength={2000}
                ></textarea>
                <p className="text-sm text-gray-500 mt-1">
                  {formData.message.length} / 2000 caractères
                </p>
              </div>

              {/* Messages de statut */}
              {status.error && (
                <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {status.error}
                </div>
              )}

              {status.success && (
                <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                  ✓ Message envoyé avec succès ! Nous vous répondrons rapidement.
                </div>
              )}

              {/* Bouton */}
              <button
                type="submit"
                disabled={status.loading}
                className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-colors ${status.loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary-600 hover:bg-primary-700'
                  }`}
              >
                {status.loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Envoi en cours...
                  </span>
                ) : (
                  'Envoyer le message'
                )}
              </button>
            </form>
          </div>

          {/* Image décorative */}
          <div className="rounded-lg overflow-hidden shadow-lg w-full mt-12">
            <img
              src={`${process.env.PUBLIC_URL}/baboimmo_contact_photo.png`}
              alt="Bureau JVG7"
              className="w-full h-80 object-cover"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
